import { z } from 'zod';
import { RECORD_ID } from './import.ts';
import { readMoment, type KeyedRead } from './history.ts';

// ITCBot's archive (plan 5F, R4, ADR 0019): its `action_logs` and `commands` collections and two ledger files, kept as they were in
// `members_archive`, read-only, for `members.admin`. A log's fields are the ones the old console filtered by; a command's
// type and status take the `action` and `outcome` columns, so the page filters both the same way. `data` is the row as it was,
// whole. As pure functions: what one old row becomes. The refusal for a row names its id and the reason, never what it said.

export const ARCHIVE_KINDS = ['action_log', 'command', 'credits', 'contributors'] as const;
export type ArchiveKind = (typeof ARCHIVE_KINDS)[number];

export const ARCHIVE_KIND_LABELS: Readonly<Record<ArchiveKind, string>> = {
  action_log: 'Action log',
  command: 'Commands',
  credits: 'Recruiter credits',
  contributors: 'Lead contributors',
};

/** The two ledgers: a file each, kept as one document under its path. */
export const ARCHIVE_FILES: Readonly<Record<'credits' | 'contributors', string>> = {
  credits: 'recruiter/recruiter_credits.json',
  contributors: 'recruiter/lead_contributors.json',
};

/** The old console's filters, in its order: the values it offered for each. */
export const LOG_CATEGORIES = ['role', 'verification', 'config', 'ping', 'rsi', 'member', 'command', 'system'] as const;
export const LOG_SEVERITIES = ['info', 'warning', 'error'] as const;
export const LOG_OUTCOMES = ['success', 'failed', 'not_found', 'skipped'] as const;

/** What a text column holds at most, and what a row's `data` may be, in bytes of JSON. A larger value is refused, never cut. */
export const MAX_TEXT = 500;
export const MAX_DATA_BYTES = 400_000;

/** One row to import. `id` is the old record id for a log or a command, and the file's path for a ledger. */
export const ArchiveImportRow = z.object({
  id: z.string().min(1).max(200),
  kind: z.enum(ARCHIVE_KINDS),
  data: z.unknown().optional(),
});
export type ArchiveImportRow = z.infer<typeof ArchiveImportRow>;

export interface ArchiveFields {
  kind: ArchiveKind;
  old_id: string;
  at: Date | null;
  category: string;
  severity: string;
  outcome: string;
  actor: string;
  target: string;
  action: string;
  data: unknown;
}

const isObject = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);
const bytes = (value: unknown): number => new TextEncoder().encode(JSON.stringify(value)).length;

/** A text column as the old row had it: a string as it is, a whole number as its digits, anything else blank. */
const column = (value: unknown): string => (typeof value === 'string' ? value : typeof value === 'number' && Number.isSafeInteger(value) ? String(value) : '');

/** The old console's short target for a command: `query`, else `handle`, else `thread_id`. `params` is an object, or JSON text of one. */
function commandTarget(params: unknown): string {
  let own = params;
  if (typeof own === 'string') {
    try {
      own = JSON.parse(own) as unknown;
    } catch {
      return '';
    }
  }
  if (!isObject(own)) return '';
  for (const name of ['query', 'handle', 'thread_id']) {
    const found = column(own[name]);
    if (found !== '') return found;
  }
  return '';
}

/**
 * What a refusal calls a row: its id when that is a record id, a ledger's path, or the page's own `row-<n>` for a row with no usable id. Anything else
 * could be whatever a file held, and a refusal names an id only.
 */
export function archiveName(raw: ArchiveImportRow): string {
  return RECORD_ID.test(raw.id) || raw.id === ARCHIVE_FILES.credits || raw.id === ARCHIVE_FILES.contributors || /^row-\d{1,9}$/.test(raw.id) ? raw.id : '(an id that isn’t a record id)';
}

/** The key an archived row is kept under: its kind and old id. */
export const archiveKey = (kind: ArchiveKind, oldId: string): string => `${kind}\u0000${oldId}`;

/**
 * R4's table for `action_logs`, `commands` and the two ledgers. A log or a command: its id must be a record id, its data an object and its
 * moment (`ts`, else `created`, for a log; `created` for a command) readable. A ledger: its id must be its file's path, and its data is
 * whatever JSON the file held. Any text that wouldn't fit its column, and any data too large to keep, is refused. A row already stored
 * under its kind and id is skipped.
 */
export function readArchive(raw: ArchiveImportRow): KeyedRead<ArchiveFields> {
  const refuse = (reason: string): KeyedRead<ArchiveFields> => ({ kind: 'refuse', reason });
  const blank = { category: '', severity: '', outcome: '', actor: '', target: '', action: '' };

  if (raw.kind === 'credits' || raw.kind === 'contributors') {
    if (raw.id !== ARCHIVE_FILES[raw.kind]) return refuse('Its id isn’t the file it belongs to');
    if (raw.data === undefined || raw.data === null) return refuse('It has nothing in it');
    if (bytes(raw.data) > MAX_DATA_BYTES) return refuse('Its data is too large to keep');
    return { kind: 'row', key: archiveKey(raw.kind, raw.id), fields: { kind: raw.kind, old_id: raw.id, at: null, ...blank, data: raw.data } };
  }

  if (!RECORD_ID.test(raw.id)) return refuse('Its id isn’t a record id');
  const row = raw.data;
  if (!isObject(row)) return refuse('It isn’t a row');
  const when = raw.kind === 'action_log' ? (row['ts'] === undefined || row['ts'] === null || row['ts'] === '' ? row['created'] : row['ts']) : row['created'];
  const at = readMoment(when);
  if (at === null) return refuse('Its date can’t be read');

  const fields: ArchiveFields =
    raw.kind === 'action_log'
      ? {
          kind: 'action_log',
          old_id: raw.id,
          at,
          category: column(row['category']),
          severity: column(row['severity']),
          outcome: column(row['outcome']),
          actor: column(row['actor']),
          target: column(row['target']),
          action: column(row['action']),
          data: row,
        }
      : {
          kind: 'command',
          old_id: raw.id,
          at,
          category: '',
          severity: '',
          outcome: column(row['status']),
          actor: column(row['requested_by_discord_id']),
          target: commandTarget(row['params']),
          action: column(row['type']),
          data: row,
        };
  for (const name of ['category', 'severity', 'outcome', 'actor', 'target', 'action'] as const) {
    if (fields[name].length > MAX_TEXT) return refuse(`Its ${raw.kind === 'command' && name === 'outcome' ? 'status' : raw.kind === 'command' && name === 'action' ? 'type' : name} is longer than ${String(MAX_TEXT)} characters`);
  }
  if (bytes(row) > MAX_DATA_BYTES) return refuse('Its data is too large to keep');
  return { kind: 'row', key: archiveKey(raw.kind, raw.id), fields };
}

/** The key a row would be stored under, or null if it can't be read. For the browser's dry run. */
export function archiveKeyOf(raw: ArchiveImportRow): string | null {
  const read = readArchive(raw);
  return read.kind === 'row' ? read.key : null;
}

/** The file's value as a document row, or null when the export didn't hold the file. */
export function ledgerRow(kind: 'credits' | 'contributors', file: unknown): ArchiveImportRow | null {
  return file === undefined ? null : { id: ARCHIVE_FILES[kind], kind, data: file };
}
